import { useRolodexEdit } from 'features/fde'
import {
  DocumentTableRow,
  transformDocumentsToTableData,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { useGeckoNotifications } from 'features/notifications'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexHistogram,
  useRolodexSignedURLs,
} from 'features/rolodex'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  BoundingBoxJsonItem,
  DocumentOcrEntry,
  DocumentOcrPage,
  KnowledgeDocumentEntry,
  RolodexEntryType,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
  RolodexSignedURLResult,
  SystemDocumentEntry,
} from 'types'

import {
  TooltipOption,
  useKnowledgeDocumentUXContext,
} from './KnowledgeDocumentUXContext'

export type DocumentMetadataUpdate = {
  title?: string
  system?: string
  vendor_name?: string
  document_type?: string
  drawing_number?: string
  description?: string
  revision_code?: string
  revision_date?: string
}

interface KnowledgeDocumentDataContextType {
  boundingBoxDataState: [DocumentOcrPage, DocumentOcrPage]
  setBoundingBoxDataState: (data: [DocumentOcrPage, DocumentOcrPage]) => void
  systemDocumentEntries: SystemDocumentEntry[]
  documentSignedURL: RolodexSignedURLResult | undefined
  saveToRolodex: (newState: DocumentOcrPage) => Promise<void>
  updateBoxLabel: (boxId: string, newLabel: string) => Promise<void>
  updateDocumentMetadata: (
    documentId: string,
    metadata: DocumentMetadataUpdate
  ) => Promise<void>
  orgID: string
  siteID: string
  knowledgeDocumentEntriesForSelectedDocument: KnowledgeDocumentEntry[]
  selectedDocumentOcrEntries: DocumentOcrEntry[] | undefined
  totalPages: number
  advancedSearchTableData: DocumentTableRow[]
  isLoadingDocuments: boolean
  uniqueAssetHistogram?: RolodexHistogramResponse[]
  allAssetHistogram?: RolodexHistogramResponse[]
}

interface KnowledgeDocumentDataProviderProps {
  children: React.ReactNode
  siteID: string
  orgID: string
}

export const KnowledgeDocumentDataProvider: React.FC<
  KnowledgeDocumentDataProviderProps
> = ({ children, siteID, orgID }) => {
  const { setTooltipOptions, currentDocumentId, currentPageNumber } =
    useKnowledgeDocumentUXContext()
  const { rolodexPatch } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()

  const [boundingBoxDataState, setBoundingBoxDataState] = useState<
    [DocumentOcrPage, DocumentOcrPage]
  >([
    { pageNumber: 1, confirmed: [], unconfirmed: [] },
    { pageNumber: 2, confirmed: [], unconfirmed: [] },
  ])

  const [advancedSearchTableData, setAdvancedSearchTableData] = useState<
    DocumentTableRow[]
  >([])

  const {
    data: systemDocumentEntries,
    mutate: refreshSystemDocuments,
    isLoading: isLoadingDocuments,
  } = useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>({
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: { site: siteID },
    limit: 100000,
  })

  const { data: selectedDocumentOcrEntries } =
    useGetRolodexEntriesByLoadOptions<DocumentOcrEntry[]>({
      type: [RolodexEntryType.DOCUMENT_OCR],
      tags: { site: siteID, 'system-document': currentDocumentId },
      limit: 1,
    })

  const { data: knowledgeDocumentEntriesForSelectedDocument } =
    useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>({
      type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
      tags: {
        site: siteID,
        'system-document': currentDocumentId,
      },
      limit: currentDocumentId ? 1000000 : 0,
    })

  const selectedDocument = useMemo(() => {
    return systemDocumentEntries?.find(
      (document) => document.entry.id === currentDocumentId
    )
  }, [systemDocumentEntries, currentDocumentId])

  const { results: documentSignedURL } = useRolodexSignedURLs(
    currentDocumentId
      ? {
          entry_id: currentDocumentId,
          path: selectedDocument?.entry.data?.filename ?? '',
        }
      : undefined
  )

  const totalPages = useMemo(() => {
    const totalPages = selectedDocument?.entry.data?.total_pages ?? 0
    return totalPages === 0 ? 1 : totalPages
  }, [selectedDocument])

  useEffect(() => {
    if (totalPages > 1) {
      setTooltipOptions((prev) => [...prev, TooltipOption.ShowMultiplePages])
    }
  }, [totalPages, setTooltipOptions])

  useEffect(() => {
    if (currentDocumentId) {
      setBoundingBoxDataState([
        selectedDocumentOcrEntries?.[0]?.entry.data?.pages?.find(
          (p) => p.pageNumber === currentPageNumber
        ) ?? {
          pageNumber: 1,
          confirmed: [],
          unconfirmed: [],
        },
        selectedDocumentOcrEntries?.[0]?.entry.data?.pages?.find(
          (p) => p.pageNumber === currentPageNumber + 1
        ) ?? {
          pageNumber: 2,
          confirmed: [],
          unconfirmed: [],
        },
      ])
    }
  }, [currentDocumentId, currentPageNumber, selectedDocumentOcrEntries])

  useEffect(() => {
    if (systemDocumentEntries) {
      setAdvancedSearchTableData(
        transformDocumentsToTableData(systemDocumentEntries)
      )
    }
  }, [systemDocumentEntries])

  const { data: uniqueAssetHistogramData } = useRolodexHistogram({
    type: RolodexEntryType.UNIT,
    select: [
      {
        field: 'data',
        key: 'custom_fields.0.inputValue',
        label: 'asset_type',
        group_by: true,
      },
      {
        op: 'count',
        field: 'id',
      },
    ],
    tags: {
      site: siteID,
    },
  } as RolodexHistogramQuery)
  const { data: allAssetHistogramData } = useRolodexHistogram({
    type: RolodexEntryType.KNOWLEDGE_DOCUMENT,
    links: [RolodexEntryType.UNIT],
    select: [
      {
        type: RolodexEntryType.UNIT,
        field: 'data',
        key: 'custom_fields.0.inputValue',
        label: 'asset_type',
        group_by: true,
      },
      {
        op: 'count',
        field: 'id',
      },
    ],
    tags: {
      site: siteID,
    },
  } as RolodexHistogramQuery)

  const uniqueAssetHistogram = useMemo(() => {
    return uniqueAssetHistogramData
  }, [uniqueAssetHistogramData])

  const allAssetHistogram = useMemo(() => {
    return allAssetHistogramData
  }, [allAssetHistogramData])

  const saveToRolodex = async (
    newState: DocumentOcrPage,
    pageIndex: number = 0
  ) => {
    const selectedOcrEntry = selectedDocumentOcrEntries?.[0]

    if (!selectedOcrEntry) {
      return
    }

    const newPages = selectedOcrEntry.entry.data?.pages.map(
      (page: DocumentOcrPage) => {
        if (page.pageNumber === currentPageNumber + pageIndex) {
          return newState
        }
        return page
      }
    )
    try {
      await rolodexPatch([
        {
          id: selectedOcrEntry.entry.id,
          data: {
            ...selectedOcrEntry.entry.data,
            pages: newPages,
          },
        },
      ])
      successNotification('Saved to Rolodex')
    } catch (error) {
      console.error('Error saving to Rolodex:', error)
      errorNotification('Failed to save to Rolodex')
      throw error
    }
  }

  const updateBoxLabel = async (boxId: string, newLabel: string) => {
    const boxIdParts = boxId.split('-')
    const pageIndex = boxIdParts.length === 2 ? Number(boxIdParts[1]) : 0
    const boxNumericId = boxIdParts.length === 2 ? Number(boxIdParts[0]) : null
    const currentPageData = boundingBoxDataState[pageIndex]

    const updateBoxInList = (boxes: BoundingBoxJsonItem[]) =>
      boxes.map((b) => (b.id === boxNumericId ? { ...b, text: newLabel } : b))

    const updatedConfirmed = updateBoxInList(currentPageData.confirmed)
    const updatedUnconfirmed = updateBoxInList(currentPageData.unconfirmed)

    const updatedPageData = {
      ...currentPageData,
      confirmed: updatedConfirmed,
      unconfirmed: updatedUnconfirmed,
    }

    const newState: [DocumentOcrPage, DocumentOcrPage] = [
      pageIndex === 0 ? updatedPageData : boundingBoxDataState[0],
      pageIndex === 1 ? updatedPageData : boundingBoxDataState[1],
    ]
    setBoundingBoxDataState(newState)
    await saveToRolodex(updatedPageData, pageIndex)
  }

  const updateDocumentMetadata = async (
    documentId: string,
    metadata: DocumentMetadataUpdate
  ) => {
    const documentEntry = systemDocumentEntries?.find(
      (entry) => entry.entry.id === documentId
    )

    if (!documentEntry) {
      errorNotification('Document not found')
      return
    }

    try {
      await rolodexPatch([
        {
          id: documentId,
          data: {
            ...documentEntry.entry.data,
            ...metadata,
          },
        },
      ])
      await refreshSystemDocuments()
      successNotification('Document metadata updated')
    } catch (error) {
      console.error('Error updating document metadata:', error)
      errorNotification('Failed to update document metadata')
      throw error
    }
  }

  const contextValue: KnowledgeDocumentDataContextType = {
    boundingBoxDataState,
    setBoundingBoxDataState,
    systemDocumentEntries: systemDocumentEntries ?? [],
    documentSignedURL: documentSignedURL?.[0],
    saveToRolodex,
    updateBoxLabel,
    updateDocumentMetadata,
    orgID,
    siteID,
    knowledgeDocumentEntriesForSelectedDocument:
      knowledgeDocumentEntriesForSelectedDocument ?? [],
    selectedDocumentOcrEntries,
    totalPages,
    advancedSearchTableData,
    isLoadingDocuments,
    uniqueAssetHistogram,
    allAssetHistogram,
  }

  return (
    <KnowledgeDocumentDataContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentDataContext.Provider>
  )
}

const KnowledgeDocumentDataContext = createContext<
  KnowledgeDocumentDataContextType | undefined
>(undefined)

export const useKnowledgeDocumentDataContext =
  (): KnowledgeDocumentDataContextType => {
    const context = useContext(KnowledgeDocumentDataContext)
    if (context === undefined) {
      throw new Error(
        'useKnowledgeDocumentDataContext must be used within a KnowledgeDocumentDataProvider'
      )
    }
    return context
  }
